import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';
import { formatFromFilter, formatToFilter } from '@app/utils/formatDate';

interface StockingPlanFilterState {
  selectedFishtypeIds: number[];
  selectedWaterbodyIds: number[];
  fromDate: Dayjs | null | undefined;
  toDate: Dayjs | null | undefined;
  recurring: boolean | undefined;
  selectedFishtypeIdsTemporal: number[];
  selectedWaterbodyIdsTemporal: number[];
  fromDateTemporal: Dayjs | null | undefined;
  toDateTemporal: Dayjs | null | undefined;
  recurringTemporal: boolean | undefined;
  shouldTriggerFetch: boolean;
  filterOptions: {
    uniqueFishtypes: Array<{ id: number; name_DE: string }>;
    uniqueWaterbodies: Array<{ id: number; nameDe: string }>;
  };
}

const initialState: StockingPlanFilterState = {
  selectedFishtypeIds: [],
  selectedWaterbodyIds: [],
  fromDate: undefined,
  toDate: undefined,
  recurring: undefined,
  selectedFishtypeIdsTemporal: [],
  selectedWaterbodyIdsTemporal: [],
  fromDateTemporal: undefined,
  toDateTemporal: undefined,
  recurringTemporal: undefined,
  shouldTriggerFetch: true,
  filterOptions: {
    uniqueFishtypes: [],
    uniqueWaterbodies: [],
  },
};

const stockingPlanFiltersSlice = createSlice({
  name: 'stockingPlanFilters',
  initialState,
  reducers: {
    setSelectedFishtypeIds(state, action: PayloadAction<number[]>) {
      state.selectedFishtypeIds = action.payload;
    },
    setSelectedWaterbodyIds(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setFromDate(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromDate = action.payload;
    },
    setToDate(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toDate = action.payload;
    },
    setRecurring(state, action: PayloadAction<boolean | undefined>) {
      state.recurring = action.payload;
    },
    setSelectedFishtypeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedFishtypeIdsTemporal = action.payload;
    },
    setSelectedWaterbodyIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIdsTemporal = action.payload;
    },
    setFromDateTemporal(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromDateTemporal = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToDateTemporal(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toDateTemporal = action.payload;
      state.shouldTriggerFetch = true;
    },
    setRecurringTemporal(state, action: PayloadAction<boolean | undefined>) {
      state.recurringTemporal = action.payload;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.selectedFishtypeIds = initialState.selectedFishtypeIds;
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.fromDate = initialState.fromDate;
      state.toDate = initialState.toDate;
      state.recurring = initialState.recurring;
      state.selectedFishtypeIdsTemporal = initialState.selectedFishtypeIdsTemporal;
      state.selectedWaterbodyIdsTemporal = initialState.selectedWaterbodyIdsTemporal;
      state.fromDateTemporal = initialState.fromDateTemporal;
      state.toDateTemporal = initialState.toDateTemporal;
      state.recurringTemporal = initialState.recurringTemporal;
    },
    applyFilters(state) {
      state.selectedFishtypeIds = state.selectedFishtypeIdsTemporal;
      state.selectedWaterbodyIds = state.selectedWaterbodyIdsTemporal;
      state.fromDate = state.fromDateTemporal;
      state.toDate = state.toDateTemporal;
      state.recurring = state.recurringTemporal;
      state.shouldTriggerFetch = true;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setFilterOptions(
      state,
      action: PayloadAction<{
        uniqueFishtypes: Array<{ id: number; name_DE: string }>;
        uniqueWaterbodies: Array<{ id: number; nameDe: string }>;
      }>,
    ) {
      state.filterOptions = action.payload;
    },
  },
});

export const {
  setSelectedFishtypeIds,
  setSelectedWaterbodyIds,
  setFromDate,
  setToDate,
  setRecurring,
  setSelectedFishtypeIdsTemporal,
  setSelectedWaterbodyIdsTemporal,
  setFromDateTemporal,
  setToDateTemporal,
  setRecurringTemporal,
  resetFilters,
  applyFilters,
  setShouldTriggerFetch,
  setFilterOptions,
} = stockingPlanFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { stockingPlanFilters: StockingPlanFilterState }) => {
  const { selectedFishtypeIds, selectedWaterbodyIds, fromDate, toDate, recurring } = state.stockingPlanFilters;

  return (
    selectedFishtypeIds.length > 0 ||
    selectedWaterbodyIds.length > 0 ||
    !!fromDate ||
    !!toDate ||
    recurring !== undefined
  );
};

export const activeFiltersCountSelector = (state: { stockingPlanFilters: StockingPlanFilterState }) => {
  const { selectedFishtypeIds, selectedWaterbodyIds, fromDate, toDate, recurring } = state.stockingPlanFilters;

  let count = 0;
  if (selectedFishtypeIds.length > 0) count++;
  if (selectedWaterbodyIds.length > 0) count++;
  if (fromDate) count++;
  if (toDate) count++;
  if (recurring !== undefined) count++;

  return count;
};

export const fetchParamsSelector = (state: { stockingPlanFilters: StockingPlanFilterState }) => {
  const {
    selectedFishtypeIdsTemporal,
    selectedWaterbodyIdsTemporal,
    fromDateTemporal,
    toDateTemporal,
    recurringTemporal,
  } = state.stockingPlanFilters;

  const params: {
    fishtypeIds?: number[];
    waterbodyIds?: number[];
    start?: string;
    end?: string;
    recurring?: boolean;
  } = {};

  // Only include filters when they have meaningful values
  if (selectedFishtypeIdsTemporal.length > 0) {
    params.fishtypeIds = selectedFishtypeIdsTemporal;
  }

  if (selectedWaterbodyIdsTemporal.length > 0) {
    params.waterbodyIds = selectedWaterbodyIdsTemporal;
  }

  if (fromDateTemporal) {
    params.start = formatFromFilter(fromDateTemporal);
  }

  if (toDateTemporal) {
    params.end = formatToFilter(toDateTemporal);
  }

  if (recurringTemporal !== undefined) {
    params.recurring = recurringTemporal;
  }

  return params;
};

export const stockingPlanFiltersActions = stockingPlanFiltersSlice.actions;

export const stockingPlanFilters = stockingPlanFiltersSlice.reducer;
